'use client'
import { Fragment, useEffect, useState } from 'react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { X, LocateFixed } from 'lucide-react'
import CommonSearchInput from '../CommonSearchInput'
import { dispatch, useSelector } from '@/lib/store'
import { updateCityData } from '@/lib/reducers/cityReducer'
import { CityObjectType, LocationPromptModalType } from '@/utils/interfaces'
import { loadState, saveState } from '@/utils/localStorage'
import useCustomToast from '@/hooks/useCustomToast'
import CART_HELPER from '@/helper/cartHelper'
import { updateCommonReducerData } from '@/lib/reducers/commonReducer'
import Image from 'next/image'
import LOCATION_HELPER from '@/helper/locationHelper'
import { isEmpty, isCityProductRoute, isNonCityRoute } from '@/utils/commonFunc'
import { usePathname, useRouter } from 'next/navigation'
import {
  CARE_PACKAGES_PAGE,
  DISEASE_CATEGORIES_PAGES,
  EDOS_PAGE,
  HEALTH_CHECKUP_LISTING_PAGE,
  NON_CITY_PRODUCT_PATH,
} from '@/utils/constants'

// Sub-route names that can occupy the [city] slot on 2-segment listing URLs.
const NON_CITY_URL_SEGMENTS = ['categories', 'disease-categories', 'packages']

const LocationPromptModal = ({
  open = false,
  onOpenChange,
  autoDetect = false,
}: LocationPromptModalType) => {
  const showToast = useCustomToast()
  const pathname = usePathname()
  const router = useRouter()

  const { reverseGeocoding, handleLatLongServiceable, handleGetCityListData } =
    LOCATION_HELPER()

  const cityState = useSelector((state) => state?.city) || {}
  const cartState: any = useSelector((state) => state.cart) || {}
  const commonState = useSelector((state) => state.common) || {}
  const cityLocalStorageData = loadState('city') || {}
  const HAS_USER_DENIED_FOR_LOCATION_ACCESS =
    loadState('user_denied_location') || false

  const [loading, setLoading] = useState(false)
  const [componentLoading, setComponentLoading] = useState(false)
  const [cityData, setCityData] = useState<any>({})

  let ALL_CITY_LIST: any = cityState?.allCitiesList || []

  const [cityDataToDisplay, setCityDataToDisplay] = useState(cityData)
  const [cityQuery, setCityQuery] = useState<string>('')
  const [permissionStatus, setPermissionStatus] = useState('')
  const [isAutoDetecting, setIsAutoDetecting] = useState(false)

  const handleAskLocationPermission = () => {
    if (permissionStatus === 'denied') {
      setIsAutoDetecting(false)
      onOpenChange(false)
      showToast(
        'danger',
        'Location access is disabled. Please enable it in your browser settings for a better experience.'
      )
      return
    }
    if (!!navigator.geolocation) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          if (result.state === 'granted' || result.state === 'prompt') {
            getUsersLocation()
            setPermissionStatus(result.state)
            return
          }
          if (result.state === 'denied') {
            setIsAutoDetecting(false)
            showToast(
              'danger',
              'Location access is disabled. Please enable it in your browser settings for a better experience.'
            )
            setLoading(false)
            setPermissionStatus('denied')
            saveState('user_denied_location', true)
            if (
              Object.keys(cityLocalStorageData)?.length > 0 &&
              Object?.values(cityLocalStorageData).every((item) => item === '')
            ) {
              onOpenChange(true)
            } else {
              onOpenChange(false)
            }
          }
        })
        .catch((err) => {
          console.error(`Error checking permissions: ${err}`)
          setIsAutoDetecting(false)
          setLoading(false)
        })
    } else {
      console.error('Geolocation is not supported by this browser.')
      setIsAutoDetecting(false)
      setLoading(false)
    }
  }

  const getUsersLocation = () => {
    setLoading(true)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          handleGetCityFromLatLong(latitude, longitude)
          setLoading(false)
        },
        (error) => {
          console.error(`Error obtaining location: ${error.message}`)
          setLoading(false)
          setIsAutoDetecting(false)
          onOpenChange(false)
          saveState('user_denied_location', true)
          setPermissionStatus('denied')
        }
      )
    } else {
      console.error('Geolocation is not supported by this browser.')
      setIsAutoDetecting(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && autoDetect) {
      setIsAutoDetecting(true)
      handleAskLocationPermission()
    }
  }, [open, autoDetect])

  const handleGetCityListing = async () => {
    setComponentLoading(true)
    const { cityDataFromAPI } = await handleGetCityListData()
    setCityData(cityDataFromAPI)
    setCityDataToDisplay(cityDataFromAPI)

    setComponentLoading(false)
  }

  const handleUpdateURL = (cityData: any) => {
    const isNonCityProductPage = isNonCityRoute(pathname)
    const isCityProductPage = isCityProductRoute(pathname)
    const isEdosPage = pathname.includes(EDOS_PAGE)
    const isListingRoute = [
      HEALTH_CHECKUP_LISTING_PAGE,
      DISEASE_CATEGORIES_PAGES,
      CARE_PACKAGES_PAGE,
    ].some((segment) => pathname.includes(segment + '/'))

    // For city product URLs, only update if cart is empty
    if (isCityProductPage && !isEmpty(cartState?.cartDtoList)) return

    if (
      isNonCityProductPage ||
      isCityProductPage ||
      isListingRoute ||
      isEdosPage
    ) {
      const filteredCity = cityData?.slugName
      const currentUrl = new URL(window.location.href)
      let currentPath = currentUrl.pathname
      // Strip internal rewrite prefix if present
      if (currentPath.startsWith(NON_CITY_PRODUCT_PATH)) {
        currentPath = currentPath.replace(NON_CITY_PRODUCT_PATH, '')
      }
      const pathSegments = currentPath.split('/')

      if (pathSegments.length >= 3) {
        // On /disease/categories segment 2 is the sub-route, not a city; overwriting loses the page.
        const isCitySlot = !NON_CITY_URL_SEGMENTS.includes(pathSegments[2])
        if (isNonCityProductPage || !isCitySlot) {
          // Non-city URL (e.g. /package/200032793/slug, /disease/disease-categories/slug,
          // or /care-packages/packages/<category>) — insert city before the existing slug
          pathSegments.splice(2, 0, filteredCity)
        } else {
          pathSegments[2] = filteredCity
        }
      }
      const updatedPath = pathSegments.join('/')
      router.push(updatedPath)
    }
  }

  const handleGetCityFromLatLong = async (latitude: any, longitude: any) => {
    if (!latitude || !longitude) {
      setIsAutoDetecting(false)
      return
    }
    // const reverseResponse: any = await reverseGeocoding(latitude, longitude)

    const latLngServicableResponse =
      (await handleLatLongServiceable(latitude, longitude)) || {}
    const latLngServicableData = latLngServicableResponse?.data || {}

    const cityObj =
      ALL_CITY_LIST?.find(
        (item: any) => item?.uuid === latLngServicableData?.cityUuid
      ) || null

    if (
      Object.keys(latLngServicableResponse)?.length === 0 ||
      latLngServicableResponse?.status === false ||
      latLngServicableData?.servicable === false ||
      isEmpty(cityObj)
    ) {
      setLoading(false)
      setIsAutoDetecting(false)
      onOpenChange(false)
      dispatch(
        updateCommonReducerData({
          ...commonState,
          pincodeNotServicableModalIsOpen: true,
        })
      )
      return
    }
    if (latLngServicableResponse?.status) {
      if (
        latLngServicableData?.cityUuid !== cityState?.currentCityObj?.uuid &&
        cartState?.cartDtoList[0]?.productDtoList?.length > 0
      ) {
        dispatch(
          updateCommonReducerData({
            ...commonState,
            updatedCityUuid: latLngServicableData?.cityUuid,
          })
        )
        setIsAutoDetecting(false)
        onOpenChange(false)
        return
      }

      handleUpdateURL(cityObj)

      if (!!latLngServicableData?.cityUuid) {
        dispatch(
          updateCityData({
            ...cityState,
            currentCityObj: cityObj,
            allCitiesList: ALL_CITY_LIST,
            isCityServicable: true,
          })
        )
        saveState('city', cityObj)
        // saveState('selectedPincode', postal_code)
      } else {
        dispatch(
          updateCityData({
            ...cityState,
            currentCityObj: {},
            allCitiesList: ALL_CITY_LIST,
            isCityServicable: false,
          })
        )
      }
      setIsAutoDetecting(false)
      onOpenChange(false)
      setCityQuery('')
      setCityDataToDisplay(cityData)
      return
    } else {
      setIsAutoDetecting(false)
      console.error('Failed to fetch', latLngServicableResponse)
    }
  }

  const handleSearchFromTheCityList = async (data: any, query: string) => {
    let filteredCity =
      data?.length > 0 &&
      data.filter((item: CityObjectType) =>
        item?.name?.toLocaleLowerCase()?.includes(query?.toLocaleLowerCase())
      )
    return filteredCity
  }

  const handleSearchCity = async (event: any) => {
    const value = event.target.value
    setCityQuery(value)

    if (!value) {
      setCityDataToDisplay(cityData)
      return
    }

    const popularFilteredCity = await handleSearchFromTheCityList(
      cityData?.popularCityList,
      value
    )
    const nonPopularFilteredCity = await handleSearchFromTheCityList(
      cityData?.nonPopularCityList,
      value
    )

    setCityDataToDisplay({
      popularCityList: popularFilteredCity,
      nonPopularCityList: nonPopularFilteredCity,
    })
  }

  const handleSelectCity = async (cityDataObj: any) => {
    await handleUpdateCartByChangingCityUuid(cityDataObj?.uuid)
    saveState('selectedPincode', null)

    handleUpdateURL(cityDataObj)

    if (isEmpty(cartState?.cartDtoList)) {
      dispatch(
        updateCityData({
          ...cityState,
          currentCityObj: cityDataObj,
          isCityServicable: true,
        })
      )
      saveState('city', cityDataObj)
    }

    onOpenChange(false)
    setCityQuery('')
    setCityDataToDisplay(cityData)
  }

  const handleUpdateCartByChangingCityUuid = async (cityUuid: string) => {
    if (
      cartState?.cartDtoList[0]?.productDtoList?.length > 0 &&
      cityUuid !== cityState?.currentCityObj?.uuid
    ) {
      dispatch(
        updateCommonReducerData({ ...commonState, updatedCityUuid: cityUuid })
      )
    }
  }

  // useEffect(() => {
  //   if (
  //     !permissionStatus &&
  //     permissionStatus !== 'denied' &&
  //     !HAS_USER_DENIED_FOR_LOCATION_ACCESS &&
  //     isEmpty(cityLocalStorageData)
  //   ) {
  //     handleAskLocationPermission()
  //   }
  // }, [permissionStatus])

  useEffect(() => {
    if (!open) return
    handleGetCityListing()
  }, [open])

  return (
    <div>
      <Dialog
        open={open && !isAutoDetecting}
        onOpenChange={() => {
          onOpenChange(false)
          setIsAutoDetecting(false)
          setCityQuery('')
          setCityDataToDisplay(cityData)
        }}
      >
        <DialogContent
          aria-describedby="Find your location modal"
          className="location_prompt_modal rounded-none sm:rounded-2xl p-0 gap-0 h-full sm:h-auto transition-all border-none flex flex-col max-w-[680px] w-full sm:max-h-[560px] overflow-hidden"
        >
          {/* Header */}
          <DialogHeader className="px-6 pt-6 pb-2">
            <div className="flex items-center justify-between mb-5">
              <DialogTitle className="text-body-lg font-semibold text-gray-900">
                Select Location
              </DialogTitle>
              <DialogClose onClick={() => onOpenChange(false)}>
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <X size={18} className="text-gray-700" />
                </div>
              </DialogClose>
            </div>
            <VisuallyHidden.Root>
              <DialogDescription>
                Find your location by input or detect your current location
              </DialogDescription>
            </VisuallyHidden.Root>
            <CommonSearchInput
              value={cityQuery}
              placeholder="Search for your city"
              onChange={handleSearchCity}
              containerClassName="w-full"
            />
            <button
              className="flex items-center gap-1.5 text-label-sm font-semibold text-gray-900 hover:text-gray-700 transition-colors py-4"
              onClick={() => {
                saveState('user_denied_location', false)
                handleAskLocationPermission()
              }}
              disabled={loading}
            >
              <LocateFixed size={18} className="text-primary-600" />
              {loading ? 'Fetching location...' : 'Use precise location'}
            </button>
          </DialogHeader>

          {/* Loading skeleton */}
          {componentLoading && (
            <div className="px-6 py-4">
              <div
                role="status"
                className="grid grid-cols-3 sm:grid-cols-6 gap-3 animate-pulse"
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-16 bg-gray-100 rounded-xl" />
                ))}
              </div>
            </div>
          )}

          {/* City lists */}
          {!componentLoading && (
            <div className="relative overflow-y-auto scroll-smooth flex-1">
              {/* Popular Cities */}
              {!!cityDataToDisplay?.popularCityList &&
                cityDataToDisplay?.popularCityList?.length > 0 && (
                  <div className="px-6 pt-3 pb-4">
                    <h2 className="text-body-sm font-semibold text-gray-700 mb-3">
                      Popular Cities
                    </h2>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
                      {cityDataToDisplay?.popularCityList?.map(
                        (location: any) => {
                          const isActive =
                            cityState?.currentCityObj?.uuid === location?.uuid
                          return (
                            <button
                              key={location?.uuid}
                              className={`flex flex-col items-center justify-center rounded-xl px-3 py-3.5 transition-all duration-150 cursor-pointer border ${
                                isActive
                                  ? 'bg-primary-50 border-primary-500'
                                  : 'bg-white border-gray-200 shadow-sm hover:shadow hover:scale-[1.03]'
                              }`}
                              onClick={() => handleSelectCity(location)}
                            >
                              {location?.iconUrl && (
                                <Image
                                  src={location.iconUrl}
                                  alt={location?.name}
                                  className="transition-all duration-150"
                                  width={56}
                                  height={44}
                                  placeholder="blur"
                                  blurDataURL={location.iconUrl}
                                />
                              )}
                              <p
                                className={`text-label-sm leading-4 mt-2 text-center ${
                                  isActive
                                    ? 'text-primary-700 font-semibold'
                                    : 'text-gray-900 font-medium'
                                }`}
                              >
                                {location?.name}
                              </p>
                            </button>
                          )
                        }
                      )}
                    </div>
                  </div>
                )}

              {/* All Cities */}
              {!!cityDataToDisplay?.nonPopularCityList &&
                cityDataToDisplay?.nonPopularCityList?.length > 0 && (
                  <div className="px-6 pt-1 pb-5">
                    <h2 className="text-body-sm font-semibold text-gray-700 mb-2">
                      All Cities
                    </h2>
                    <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-0.5">
                      {(cityDataToDisplay?.nonPopularCityList || []).map(
                        (city: any) => {
                          const isActive =
                            cityState?.currentCityObj?.name === city?.name
                          return (
                            <li key={city?.uuid}>
                              <button
                                className={`w-full text-start capitalize text-body-sm py-1.5 px-2.5 rounded-md transition-colors duration-150 ${
                                  isActive
                                    ? 'text-primary-600 font-medium bg-primary-50'
                                    : 'text-gray-700 hover:bg-primary-50 hover:text-primary-600'
                                }`}
                                onClick={() => handleSelectCity(city)}
                              >
                                {city?.name}
                              </button>
                            </li>
                          )
                        }
                      )}
                    </ul>
                  </div>
                )}

              {/* No results */}
              {cityDataToDisplay?.popularCityList?.length == 0 &&
                cityDataToDisplay?.nonPopularCityList?.length == 0 && (
                  <div className="flex items-center justify-center py-10 px-6">
                    <p className="text-body-sm text-gray-500">
                      No city found for &lsquo;{cityQuery}&rsquo;
                    </p>
                  </div>
                )}

              {/* Bottom scroll fade */}
              <div className="sticky bottom-0 left-0 right-0 h-5 bg-gradient-to-t from-white to-transparent pointer-events-none" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default LocationPromptModal
